import React from 'react';

export const USER_ROLES = {
  FREELANCER: 'freelancer',
  LIGHT_ENTERPRENEUR: 'light-entrepreneur',
  CUSTOMER: 'customer',
};

export const PROFILE_LIVE_THRESHOLD = 65;
export const MAX_PROFILE_POINTS = 100;
export const MAX_PORTFOLIO_POINTS = 60;
export const MAX_TOTAL_SCORE = MAX_PROFILE_POINTS + MAX_PORTFOLIO_POINTS;

// ================ Point system ================ //

export const computeProfileData = currentUser => {
  const profile = currentUser?.attributes?.profile;
  const publicData = profile?.publicData || {};
  const metadata = profile?.metadata || {};
  const bio = profile?.bio;

  const profileImage = !!currentUser?.profileImage?.id?.uuid;
  const whoAmI = !!publicData.whoAmI;
  const languages = Array.isArray(publicData.languages) && publicData.languages.length > 0;
  const bioDone = !!bio && bio !== '<p><br></p>';
  const experience =
    (Array.isArray(publicData.experienceKeys) && publicData.experienceKeys.length > 0) ||
    (Array.isArray(publicData.experience) && publicData.experience.length > 0);
  const tools = Array.isArray(publicData.tools) && publicData.tools.length > 0;
  const workExperience =
    Array.isArray(publicData.workExperience) && publicData.workExperience.length > 0;
  const workedWith = Array.isArray(publicData.workedWith) && publicData.workedWith.length > 0;
  // Private entries don't count towards points or activation — flag the case
  // where the user has only private ones so the UI can explain why the
  // section still shows as unfilled.
  const privateWorkExperienceOnly =
    !workExperience &&
    Array.isArray(publicData.privateWorkExperience) &&
    publicData.privateWorkExperience.length > 0;
  const privateWorkedWithOnly =
    !workedWith &&
    Array.isArray(publicData.privateWorkedWith) &&
    publicData.privateWorkedWith.length > 0;
  const hourlyFee = !!publicData.hourlyFee;
  const education = Array.isArray(publicData.education) && publicData.education.length > 0;
  const hobbies =
    Array.isArray(publicData.hobbies) &&
    publicData.hobbies.length > 0 &&
    publicData.hobbies[0]?.category !== null;
  const cocAccepted = !!publicData.cocAccepted;
  const verified = !!metadata.verified;

  return {
    profileImage,
    whoAmI,
    languages,
    bio: bioDone,
    experience,
    tools,
    workExperience,
    workedWith,
    privateWorkExperienceOnly,
    privateWorkedWithOnly,
    hourlyFee,
    education,
    hobbies,
    cocAccepted,
    verified,
  };
};

// Mirrors server/api/calculate-profile-tier.js so the UI can show the same
// total without waiting for the server round-trip after each edit.
// This is the profile-completeness component only (0–100). Portfolio
// contribution is computed separately by computePortfolioPoints and combined
// in computeTotalScore.
export const computeProfilePoints = profileData => {
  let points = 0;
  if (profileData.bio) points += 10;
  if (profileData.whoAmI) points += 10;
  if (profileData.experience) points += 10;
  if (profileData.tools) points += 10;
  if (profileData.workExperience) points += 10;
  if (profileData.workedWith) points += 10;
  if (profileData.hobbies) points += 5;
  if (profileData.hourlyFee) points += 5;
  if (profileData.languages) points += 5;
  if (profileData.education) points += 5;
  if (profileData.profileImage) points += 10;
  if (profileData.cocAccepted) points += 5;
  if (profileData.verified) points += 5;
  return points;
};

// Reads server-stored portfolio points from the user's profile metadata.
// The value is set by server/api/calculate-portfolio-tier.js (10 per published
// portfolioWork listing, capped at 60).
export const computePortfolioPoints = currentUser => {
  const metadata = currentUser?.attributes?.profile?.metadata || {};
  const stored = metadata.portfolioPoints || 0;
  return Math.min(stored, MAX_PORTFOLIO_POINTS);
};

// Combined score shown in dashboard / score pill — matches the server-side
// profileTier (profilePoints + portfolioPoints).
export const computeTotalScore = currentUser => {
  const profileData = computeProfileData(currentUser);
  return computeProfilePoints(profileData) + computePortfolioPoints(currentUser);
};

export const isProfileLiveEligible = (profileData, totalPoints) => {
  const hasWorkHistory = profileData.workExperience || profileData.workedWith;
  return (
    hasWorkHistory &&
    profileData.bio &&
    profileData.experience &&
    profileData.tools &&
    totalPoints >= PROFILE_LIVE_THRESHOLD
  );
};

export const getScoreLabelId = (points, isLive) => {
  if (!isLive) {
    return points < 80 ? 'DashboardPage.scoreLabelFair' : 'DashboardPage.scoreLabelAlmostThere';
  }
  return points < MAX_TOTAL_SCORE
    ? 'DashboardPage.scoreLabelGreat'
    : 'DashboardPage.scoreLabelExcellent';
};

// ================ Circular Score SVG ================ //

export const CircularScore = ({ points, maxPoints = MAX_TOTAL_SCORE }) => {
  const radius = 25;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(points / maxPoints, 1);
  const offset = circumference * (1 - progress);

  return (
    <svg width="60" height="60" viewBox="0 0 60 60">
      <circle cx="30" cy="30" r={radius} fill="none" stroke="#E1E1E1" strokeWidth="5" />
      <circle
        cx="30"
        cy="30"
        r={radius}
        fill="none"
        stroke="#1683A3"
        strokeWidth="5"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform="rotate(-90 30 30)"
      />
      <text
        x="30"
        y="30"
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="Poppins, sans-serif"
        fontWeight="600"
        fontSize="16"
        fill="#1683A3"
      >
        {points}
      </text>
    </svg>
  );
};
